<script setup lang="ts">
import VChart from 'vue-echarts'

import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { SVGRenderer } from 'echarts/renderers'

export type ChartBarSeries = {
  name: string
  /** `null` lässt den Balken für diese Kategorie aus (z.B. Werte, die nur einer von mehreren Gruppen zugeordnet sind). */
  values: Array<number | null>
  /** Optionale Farbe: Nuxt-UI-Semantikname (`"primary"`, `"error"`, ...) oder CSS-Farbe — ohne Angabe Standard-Palette der Reihe nach. */
  color?: string
}

// Für Balkendiagramme mit Kategorien auf der X-Achse — beliebig viele
// Kategorien/Serien, vom Aufrufer bereits auf die verfügbaren Kennzahlen
// gefiltert (analog zu `charts/radar-chart.client.vue`).
const props = defineProps({
  categories: {
    type: Array as () => string[],
    default: () => [],
  },
  series: {
    type: Array as () => ChartBarSeries[],
    default: () => [],
  },
  /** Einheit für Achsen-/Tooltip-/Label-Beschriftung, z.B. `"g"`, `"s"`. */
  unit: {
    type: String,
    default: '',
  },
  height: {
    type: Number,
    default: 240,
  },
  /** Balken horizontal statt vertikal (Kategorien auf der Y-Achse) — z.B. für Vergleiche mit längeren Labels. */
  horizontal: {
    type: Boolean,
    default: false,
  },
})

use([
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  SVGRenderer,
])

// ECharts rendert auf <canvas>/SVG-Attribute und löst CSS-Variablen dort
// nicht selbst auf — wie in `charts/radar-chart.client.vue` einmalig über
// `getComputedStyle` den tatsächlichen Farbwert auslesen.
function resolveCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

const UI_SEMANTIC_COLORS = new Set(['primary', 'secondary', 'success', 'info', 'warning', 'error', 'neutral'])

// Anders als dünne Linien/15%-Flächen (siehe `charts/radar-chart.client.vue`)
// sind Balken grosse flächige Marks — die App-Standardfarben (aris-400/
// industrial-400) sind dafür laut dataviz-Palettenvalidierung zu hell
// (`validate_palette.js` FAILt den Lightness-Band-Check). Diese dunkleren
// Stufen derselben Farbfamilie bestehen ihn.
const DEFAULT_SERIES_COLORS = [
  resolveCssVar('--color-aris-700'),
  resolveCssVar('--color-industrial-600'),
  '#4f46e5',
  '#be185d',
]

function resolveColor(color: string | undefined, fallback: string): string {
  if (color && UI_SEMANTIC_COLORS.has(color)) return resolveCssVar(`--ui-${color}`)
  return color ?? fallback
}

const hasLegend = computed(() => props.series.length > 1)

const resolvedSeries = computed(() =>
  props.series.map((entry, index) => ({
    ...entry,
    color: resolveColor(entry.color, DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length]),
  })),
)

const numberFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 })

function formatValue(value: number | null | undefined): string {
  if (value == null) return '–'
  return `${numberFormat.format(value)}${props.unit ? ` ${props.unit}` : ''}`
}

const option = computed(() => ({
  tooltip: {
    trigger: 'axis',
    axisPointer: { type: 'shadow' },
    confine: true,
    backgroundColor: 'rgba(24, 24, 27, 0.96)',
    borderColor: 'rgba(255, 255, 255, 0.12)',
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: '#e5e5e5', fontSize: 12 },
    valueFormatter: (value: number | null) => formatValue(value),
  },

  legend: hasLegend.value
    ? {
        top: 0,
        padding: 0,
        itemGap: 16,
        itemWidth: 10,
        itemHeight: 10,
        textStyle: { color: '#d4d4d4', fontSize: 11 },
        data: props.series.map((entry) => entry.name),
      }
    : undefined,

  grid: {
    left: 8,
    right: 8,
    top: hasLegend.value ? 40 : 28,
    bottom: 4,
    containLabel: true,
  },

  xAxis: props.horizontal
    ? {
        type: 'value',
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.08)' } },
        axisLabel: {
          color: '#a3a3a3',
          fontSize: 11,
          formatter: (value: number) => numberFormat.format(value),
        },
      }
    : {
        type: 'category',
        data: props.categories,
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisTick: { show: false },
        axisLabel: { color: '#d4d4d4', fontSize: 11 },
      },

  yAxis: props.horizontal
    ? {
        type: 'category',
        data: props.categories,
        inverse: true,
        axisLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.15)' } },
        axisTick: { show: false },
        axisLabel: { color: '#d4d4d4', fontSize: 11 },
      }
    : {
        type: 'value',
        splitLine: { lineStyle: { color: 'rgba(255, 255, 255, 0.08)' } },
        axisLabel: {
          color: '#a3a3a3',
          fontSize: 11,
          formatter: (value: number) => numberFormat.format(value),
        },
      },

  series: resolvedSeries.value.map((entry) => ({
    name: entry.name,
    type: 'bar',
    data: entry.values,
    barMaxWidth: 42,
    // Abgerundete Enden nur oben/rechts (am Wert), an der Baseline (0) eckig — siehe dataviz-Vorgabe "rounded data-ends anchored to the baseline".
    itemStyle: { color: entry.color, borderRadius: props.horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0] },
    label: {
      show: true,
      position: props.horizontal ? 'insideLeft' : 'top',
      color: props.horizontal ? '#ffffff' : '#e5e5e5',
      fontSize: 11,
      fontWeight: props.horizontal ? 'bold' : 'normal',
      formatter: (params: { value: number | null }) => (params.value == null ? '' : formatValue(params.value)),
    },
    emphasis: {
      itemStyle: { color: entry.color, opacity: 0.85 },
    },
  })),
}))
</script>

<template>
  <div :style="{ height: `${height}px` }">
    <VChart
      :option="option"
      :init-options="{ renderer: 'svg' }"
      autoresize
    />
  </div>
</template>
